import {
  IBidsData,
  IBidsOrigin,
  IBids,
  IApiBidsData,
  IApiBids,
  IApiBidsCallOffOrigin,
  IApiBidsTenderOrigin,
  IApiBidsManualOrigin
} from './types';
import {
  isFieldCurrency,
  IBoxFieldCurrency,
  IBoxFieldString,
  IBoxFieldDate
} from 'src/models/procurements/Tender/types';
import { TenderBox } from 'src/models/procurements';
import { isNotUndefined } from 'src/helpers';
import { User } from 'src/models/users';
import { Stage } from 'src/models/workspace/WorkspaceStatus';
import { BaseWorkspace } from 'src/models/workspace';
import { filterBoxes, convertApiLotsToLots } from 'src/models/procurements/Tender/helpers';

export class BidsData implements IBidsData {
  readonly total: number;
  readonly bids: IBids[];

  constructor(private readonly apiBidsResponse: IApiBidsData) {
    this.total = apiBidsResponse.total;
    this.bids = this.toBidsData();
  }

  private toBidsData(): IBids[] {
    const apiBids = this.apiBidsResponse.bids;
    if (!apiBids) {
      return [];
    }
    return apiBids.map(apiBid => {
      return {
        id: apiBid.id,
        origin: this.toBidsOrigin(apiBid),
        status: apiBid.status ? new Stage(apiBid.status) : null,
        assignedTo: apiBid.assignedTo ? new User(apiBid.assignedTo) : null,
        workspace: new BaseWorkspace(apiBid.workspace),
        isCommented: !!apiBid.comments.length,
        isRejected: apiBid.isRejected,
        bidStageId: apiBid.bidStageId,
        itemId: apiBid.itemId
      };
    });
  }

  private toBidsOrigin(apiBid: IApiBids): IBidsOrigin {
    const origin = apiBid.item;
    const originType = apiBid.itemType;
    const general = origin.general;
    const timeline = origin.timeline;
    const lots = convertApiLotsToLots(origin.lots);
    const estBoxes = !!filterBoxes(general.contractValueBoxes, lots).length
      ? filterBoxes(general.contractValueBoxes, lots)
      : undefined;
    const currencyFields =
      ((estBoxes || [])
        .map(box => box.fields && box.fields.filter(field => isFieldCurrency(field)))
        .flat() as IBoxFieldCurrency[]) || [];
    const frameworkBox = !!filterBoxes(general.frameworkAgreement, lots).length
      ? filterBoxes(general.frameworkAgreement, lots)[0]
      : undefined;
    const nameBox = general.name ? new TenderBox(general.name) : undefined;
    const buyerBox = !!filterBoxes(general.buyerBoxes, lots).length
      ? filterBoxes(general.buyerBoxes, lots)[0]
      : undefined;
    const publishedBox = timeline?.availableDate ? new TenderBox(timeline.availableDate) : undefined;
    const deadlineBox = !!filterBoxes(timeline.deadline, lots).length
      ? filterBoxes(timeline.deadline, lots)[0]
      : undefined;
    const startBox = timeline?.contractStartAndEndStart ? new TenderBox(timeline?.contractStartAndEndStart) : undefined;
    const endBox = timeline?.contractStartAndEndEnd ? new TenderBox(timeline?.contractStartAndEndEnd) : undefined;

    return {
      id:
        (origin as IApiBidsCallOffOrigin).mailId ??
        (origin as IApiBidsTenderOrigin).id ??
        (origin as IApiBidsManualOrigin).id ??
        '',
      originType,
      name: (nameBox && nameBox.firstField && (nameBox.firstField as IBoxFieldString).string) ?? '',
      procurerTitle: (buyerBox && buyerBox.firstField && (buyerBox.firstField as IBoxFieldString).string) ?? undefined,
      estimatedValue: estBoxes ? (estBoxes[0].firstField as IBoxFieldCurrency) : undefined,
      estimatedValueInSEK: this.getEstimatedValueInCurrency(currencyFields),
      published: (publishedBox && publishedBox.firstField && (publishedBox.firstField as IBoxFieldDate).date) ?? null,
      deadline: (deadlineBox && deadlineBox.firstField && (deadlineBox.firstField as IBoxFieldDate).date) ?? null,
      contractStart: (startBox && startBox.firstField && (startBox.firstField as IBoxFieldDate).date) ?? null,
      contractEnd: (endBox && endBox.firstField && (endBox.firstField as IBoxFieldDate).date) ?? null,
      frameworkAgreement:
        (frameworkBox && frameworkBox.firstField && (frameworkBox.firstField as IBoxFieldString).string) ?? '',
      procurementStatus: (origin as IApiBidsTenderOrigin).procurementStatus ?? undefined,
      customBoxes: origin.custom.map(box => !!box && new TenderBox(box)).filter(isNotUndefined)
    };
  }

  private getEstimatedValueInCurrency(fields: IBoxFieldCurrency[], currency = 'SEK'): number {
    return fields
      .filter(field => field.currency === currency)
      .reduce((total, field) => {
        const amount = field.amount ?? 0;
        return total + amount;
      }, 0);
  }
}
